/**
 * Phase 2B.2 — Soft paywall card.
 *
 * Rendered above admin pages when the agent's billing status is inactive.
 * For an agent past their free listing, says why and offers the plan.
 * For lapsed agents, blocks the page with a hard-but-friendly subscribe card.
 *
 * The actual content gating is in ProtectedRoute / AdminLayout — this is the
 * UI component they render.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Sparkles, Lock, AlertCircle, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export default function BillingPaywall({ status, onClose }) {
  // status: { active, status, listings_used, free_listings, can_add_listing, packages }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [pkgId, setPkgId] = useState("annual");

  const packages = status?.packages || [];
  const pkg = packages.find((p) => p.id === pkgId) || packages[0];
  const annual = packages.find((p) => p.id === "annual");
  const monthly = packages.find((p) => p.id === "monthly");

  const onCheckout = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: pkg?.id || "monthly",
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      setErr(formatApiError(e));
      setBusy(false);
    }
  };

  return (
    <div data-testid="billing-paywall" className="card"
         style={{ padding: "32px 36px", marginBottom: 28, position: "relative",
                  background: "var(--surface-alt)", borderColor: "var(--border)" }}>
      <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
        <span style={{
          width: 44, height: 44, borderRadius: 12,
          background: "var(--primary)", color: "#fff",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          {status?.status === "trialing" ? <Sparkles size={20} /> : <Lock size={20} />}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 className="font-serif"
              style={{ fontSize: 22, lineHeight: 1.2, fontWeight: 500,
                       letterSpacing: "-0.01em", margin: 0, marginBottom: 6 }}>
            {status?.status === "trialing"
              ? <>Your first listing is <em style={{ fontStyle: "italic", color: "var(--secondary)" }}>free</em>, and this is your second</>
              : <>Your <em style={{ fontStyle: "italic", color: "var(--secondary)" }}>portal access</em> has expired</>
            }
          </h3>
          <p style={{ color: "var(--text-muted)", margin: 0, marginBottom: 18, lineHeight: 1.55 }}>
            {status?.status === "trialing"
              ? "Start your subscription to add this client. Your first listing stays free, and nothing you have already built changes."
              : "Renew your subscription to unlock the admin tools again. Your sellers, prep guides, and branding are safe — nothing is deleted."}
          </p>
          {monthly && annual && (
            <p data-testid="paywall-plan-hint"
               style={{ fontSize: 12, color: "var(--text-muted)", margin: "0 0 10px 0",
                        letterSpacing: "0.04em", textTransform: "uppercase" }}>
              Most agents pick annual
            </p>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            {monthly && annual && (
              <div data-testid="paywall-plan-toggle" role="tablist"
                   style={{ display: "inline-flex", padding: 3, borderRadius: 999,
                            background: "var(--surface)", border: "1px solid var(--border)",
                            gap: 0 }}>
                {[monthly, annual].map((p) => {
                  const selected = pkg?.id === p.id;
                  const monthsLabel = p.id === "annual" ? "Annual" : "Monthly";
                  return (
                    <button
                      key={p.id}
                      data-testid={`paywall-plan-${p.id}`}
                      role="tab"
                      aria-selected={selected}
                      onClick={() => setPkgId(p.id)}
                      style={{
                        padding: "8px 16px",
                        borderRadius: 999,
                        border: "none",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 500,
                        letterSpacing: "0.01em",
                        background: selected ? "var(--text)" : "transparent",
                        color: selected ? "#fff" : "var(--text-muted)",
                        transition: "background-color 160ms ease, color 160ms ease",
                      }}
                    >
                      {monthsLabel}
                      {p.id === "annual" && (
                        <span style={{ marginLeft: 6, fontSize: 11,
                                       color: selected ? "#F4C8AE" : "var(--secondary)" }}>
                          save 2 months
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            <button
              data-testid="paywall-subscribe-btn"
              className="btn-primary"
              onClick={onCheckout}
              disabled={busy}
              style={{ opacity: busy ? 0.6 : 1 }}
            >
              {busy ? (<><Loader2 size={14} className="spin" /> Loading…</>)
                    : pkg?.id === "annual"
                      ? (<>Subscribe — ${pkg?.amount?.toFixed(0) || "490"}/year</>)
                      : (<>Subscribe — ${pkg?.amount?.toFixed(0) || "49"}/month</>)}
            </button>
            <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
              Cancel anytime · secure checkout via Stripe
            </span>
          </div>
          {err && (
            <div style={{ marginTop: 14, fontSize: 14, color: "var(--error)",
                          display: "flex", alignItems: "center", gap: 8 }}>
              <AlertCircle size={14} /> {err}
            </div>
          )}
        </div>
        {status?.status === "trialing" && onClose && (
          <button
            data-testid="paywall-dismiss-btn"
            onClick={onClose}
            className="link-button"
            style={{ fontSize: 13, color: "var(--text-muted)" }}
          >
            Later
          </button>
        )}
      </div>
    </div>
  );
}


/**
 * Hook used by AdminLayout/ProtectedRoute to fetch billing once and offer a refresh.
 * Returns { status, loading, refresh }.
 *
 * Skipped entirely for sellers (they don't need billing — only the agent does).
 */
export function useBilling(user) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const isAgent = user?.role === "admin" && !user?.impersonated_by;

  const refresh = useMemo(() => async () => {
    if (!isAgent) { setStatus(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/billing/status");
      setStatus(data);
    } catch {
      // Treat fetch failures as 'unknown' — don't paywall if we can't tell.
      setStatus({ active: true, status: "unknown" });
    } finally {
      setLoading(false);
    }
  }, [isAgent]);

  useEffect(() => { refresh(); }, [refresh]);
  return { status, loading, refresh, isAgent };
}
